import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, PhoneCall } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('/');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveLink(location.pathname);
  }, [location]);

  // Simple nav items without dropdowns
  const navItems = [
    { id: 'home', name: 'Home', path: '/' },
    { id: 'about', name: 'About Paridhi', path: '/about' },
    { id: 'rooms', name: 'Rooms in Varanasi', path: '/rooms' },
    { id: 'gallery', name: 'Gallery', path: '/gallery' },
    { id: 'booking', name: 'Book Your Stay', path: '/booking' },
    { id: 'contact', name: 'Contact Us', path: '/contact' },
  ];

  const handleMobileLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* 🔵 Top Bar - Orange Gradient */}
      <div className="bg-gradient-to-r from-blue-500 to-orange-600 text-white py-1.5 px-2 w-full">
        <div className="max-w-7xl mx-auto flex justify-between items-center flex-wrap gap-3">
          
          {/* Left Side - Phone Number */}
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <Phone size={14} />
              <a href="tel:+095550 40589" className="hover:text-orange-200 transition-colors">
                +91 95550 40589
              </a>
            </div>
          </div>

          {/* Right Side - Email */}
          <div className="flex items-center gap-2">
            <Mail size={14} />
            <a href="mailto:paridhiguest@gmail.com" className="hover:text-orange-200 transition-colors">
              paridhiguest@gmail.com
            </a>
          </div>
        </div>
      </div>

      {/* 🔵 Navbar - Light Background with Orange Accents */}
      <nav className={`bg-orange-50 sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'shadow-xl py-2' : 'shadow-md py-3'
      }`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center">

            {/* Logo Section */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Paridhi Guest House Logo" 
                className="h-12 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div class="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center"><span class="text-white font-bold text-lg">PG</span></div>';
                }}
              />
              <div className="hidden sm:block">
                <h1 className="font-serif text-lg font-semibold text-gray-800 tracking-wide">
                  Paridhi Guest House
                </h1>
                <p className="text-xs text-orange-500 -mt-0.5">
                  Luxury & Comfort Redefined
                </p>
              </div>
            </Link>

            {/* Desktop Menu - Orange Active State */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-300 rounded-md ${
                    activeLink === item.path
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Book Now Button - Orange */}
              <Link
                to="/book"
                className="ml-4 bg-blue-600 hover:bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Book Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100 focus:outline-none transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu - Orange Theme */}
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-100 mt-2">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={handleMobileLinkClick}
                  className={`block px-4 py-3 text-base font-medium rounded-md transition-colors ${
                    activeLink === item.path
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Contact Info in Mobile Menu */}
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <p className="text-xs text-orange-500 uppercase tracking-wide px-4">Contact Us</p>
                <div className="px-4 py-1">
                  <p className="text-sm text-gray-700">📞 +91 95550 40589</p>
                  <p className="text-sm text-gray-700 mt-1">✉️ paridhiguest@gmail.com</p>
                </div>
              </div>
              
              <Link
                to="/booking"
                onClick={handleMobileLinkClick}
                className="mt-4 block bg-orange-500 text-white text-center px-4 py-3 rounded-md font-medium transition-colors hover:bg-orange-600"
              >
                Book Your Stay
              </Link>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;